import { ReactText } from "react";
import { GetRequestSingleItemInterface } from "interfaces/ActionReducer/GetMethod/GetRequestSingleItem";
import { GetResponseInterface } from "interfaces/ActionReducer/GetMethod/GetResponse";
import { ResetStateAction } from "components/curb/ManagerAnalyzer/Toggle/commonUtilities";

export const SHOW_FEEDBACK_LIST_VIEW = "SHOW_FEEDBACK_LIST_VIEW";
export const GET_FEEDBACK_LIST = "GET_FEEDBACK_LIST";
export const FEEDBACK_LIST_RECEIVED = "FEEDBACK_LIST_RECEIVED";
export const SET_FEEDBACK_PARAMS = "SET_FEEDBACK_PARAMS";
export const SELECTED_FEEDBACK_IDS = "SELECTED_FEEDBACK_IDS";
export const SELECTED_FEEDBACK_COLUMNS = "SELECTED_FEEDBACK_COLUMNS";
export const TOGGLE_FEEDBACK_ACTION_PERFORMED = "TOGGLE_FEEDBACK_ACTION_PERFORMED";
export const GET_FEEDBACK_OPTIONS = "GET_FEEDBACK_OPTIONS";
export const FEEDBACK_OPTIONS_RECEIVED = "FEEDBACK_OPTIONS_RECEIVED";
export const GET_FEEDBACK_TABLE_FILTERS = "GET_FEEDBACK_TABLE_FILTERS";
export const FEEDBACK_TABLE_FILTERS_RECEIVED = "FEEDBACK_TABLE_FILTERS_RECEIVED";
export const UPDATE_FEEDBACK = "UPDATE_FEEDBACK";
export const GET_SEGMENT_FEEDBACK_COMMENTS = "GET_SEGMENT_FEEDBACK_COMMENTS";
export const SEGMENT_FEEDBACK_COMMENTS_RECEIVED = "SEGMENT_FEEDBACK_COMMENTS_RECEIVED";
export const POST_SEGMENT_FEEDBACK_COMMENT = "POST_SEGMENT_FEEDBACK_COMMENT";
export const SEGMENT_FEEDBACK_COMMENT_POSTED = "SEGMENT_FEEDBACK_COMMENT_POSTED";

export interface FeedbackItem {
    id: number;
    description: string;
    street_name: string;
    link: number;
    status: "new" | "in_review" | "done";
    email: string;
    created: string;
    responded_on: string | null;
    response_category: number[];
    responder_type: number;
    comment: string | null;
    comments: FeedbackThreadItem[];
}

export interface FeedbackThreadItem {
    id: number;
    message: string;
    sender: string;
    timestamp: string;
    replies?: FeedbackThreadItem[];
}

export interface SegmentFeedbackComment {
    id: number;
    username: string;
    segment_feedback_id: number;
    created: string;
    comment: string;
    parent_comment_id: number | null;
    user_id: number;
    replies: SegmentFeedbackComment[];
}

export interface FeedbackResponseInterface {
    meta: {
        count: number;
        next: null | string;
        previous: null | string;
    };
    results: FeedbackItem[];
}

export interface ShowFeedbackListViewAction {
    type: typeof SHOW_FEEDBACK_LIST_VIEW;
    payload: { showFeedbackListView: boolean };
}

export interface GetFeedbackListAction {
    type: typeof GET_FEEDBACK_LIST;
    payload: { params: Partial<GetRequestSingleItemInterface> };
}

export interface UpdateFeedbackAction {
    type: typeof UPDATE_FEEDBACK;
    payload: {
        id: number;
        params: { status: string; comment?: string };
        afterProcess?: () => void;
    };
}

export interface GetSegmentFeedbackCommentsAction {
    type: typeof GET_SEGMENT_FEEDBACK_COMMENTS;
    payload: { segment_feedback_id: number };
}

export interface SegmentFeedbackCommentsReceivedAction {
    type: typeof SEGMENT_FEEDBACK_COMMENTS_RECEIVED;
    payload: { segmentFeedbackComments: SegmentFeedbackComment[] };
}

export interface PostSegmentFeedbackCommentAction {
    type: typeof POST_SEGMENT_FEEDBACK_COMMENT;
    payload: {
        params: {
            comment: string;
            parent_comment_id: number | null;
            segment_feedback_id: number;
        };
        afterProcess?: () => void;
    };
}

export interface SegmentFeedbackCommentPostedAction {
    type: typeof SEGMENT_FEEDBACK_COMMENT_POSTED;
}

export interface FeedbackListReceivedAction {
    type: typeof FEEDBACK_LIST_RECEIVED;
    payload: { feedbackList: FeedbackResponseInterface };
}

export interface SetFeedbackParamsAction {
    type: typeof SET_FEEDBACK_PARAMS;
    payload: { feedbackParams: Partial<GetRequestSingleItemInterface> };
}

export interface SelectedFeedbackIdsAction {
    type: typeof SELECTED_FEEDBACK_IDS;
    payload: { selectedFeedbackIDs: number[] };
}

export interface SelectedFeedbackColumnsAction {
    type: typeof SELECTED_FEEDBACK_COLUMNS;
    payload: { selectedColumns: string[] };
}

export interface ToggleFeedbackActionPerformedAction {
    type: typeof TOGGLE_FEEDBACK_ACTION_PERFORMED;
}

export interface FeedbackOption {
    id: number;
    name: string;
}

export interface FeedbackOptions {
    responder_type: FeedbackOption[];
    response_category: FeedbackOption[];
}

export interface FeedbackTableFilter {
    column_display_name: string;
    column_filter_name: string;
    is_filtering_enabled: boolean;
    is_sortable: boolean;
    values: {
        label: string;
        value: ReactText;
    }[];
}

export interface GetFeedbackOptionsAction {
    type: typeof GET_FEEDBACK_OPTIONS;
}

export interface FeedbackOptionsReceivedAction {
    type: typeof FEEDBACK_OPTIONS_RECEIVED;
    payload: { feedbackOptions: FeedbackOptions };
}

export interface GetFeedbackTableFiltersAction {
    type: typeof GET_FEEDBACK_TABLE_FILTERS;
}

export interface FeedbackTableFiltersReceivedAction {
    type: typeof FEEDBACK_TABLE_FILTERS_RECEIVED;
    payload: { feedbackTableFilters: FeedbackTableFilter[] };
}

export interface FeedbackStateInterface {
    showFeedbackListView: boolean;
    feedbackParams: Partial<GetRequestSingleItemInterface>;
    feedbackList: FeedbackResponseInterface;
    feedbackOptions: FeedbackOptions;
    feedbackTableFilters: FeedbackTableFilter[];
    isFetchingFeedback: boolean;
    selectedFeedbackIDs: number[];
    toggleFeedbackActionPerformed: boolean;
    selectedColumns: string[];
    segmentFeedbackComments: SegmentFeedbackComment[];
    isFetchingSegmentFeedbackComments: boolean;
    isPostingSegmentFeedbackComment: boolean;
}

export type FeedbackDataTypes =
    | ShowFeedbackListViewAction
    | GetFeedbackListAction
    | UpdateFeedbackAction
    | FeedbackListReceivedAction
    | SetFeedbackParamsAction
    | SelectedFeedbackIdsAction
    | SelectedFeedbackColumnsAction
    | ToggleFeedbackActionPerformedAction
    | GetFeedbackOptionsAction
    | FeedbackOptionsReceivedAction
    | GetFeedbackTableFiltersAction
    | FeedbackTableFiltersReceivedAction
    | GetSegmentFeedbackCommentsAction
    | SegmentFeedbackCommentsReceivedAction
    | PostSegmentFeedbackCommentAction
    | SegmentFeedbackCommentPostedAction
    | ResetStateAction;
